// ━━ Design Tokens ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// crab-console 设计系统的唯一来源。
// 全局统一 slate（冷灰），禁止 gray-*。

// ── 语义色 ──────────────────────────────────────────
// Badge, Alert, Toast, 状态指示器统一使用
// -50 背景, -200 边框, -700 文字, -500 图标/实心
export const STATUS = {
  success: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', icon: 'text-emerald-500', solid: 'bg-emerald-500' },
  error:   { bg: 'bg-red-50',     border: 'border-red-200',     text: 'text-red-700',     icon: 'text-red-500',     solid: 'bg-red-500' },
  warning: { bg: 'bg-amber-50',   border: 'border-amber-200',   text: 'text-amber-700',   icon: 'text-amber-500',   solid: 'bg-amber-500' },
  info:    { bg: 'bg-blue-50',    border: 'border-blue-200',     text: 'text-blue-700',    icon: 'text-blue-500',    solid: 'bg-blue-500' },
  neutral: { bg: 'bg-slate-50',   border: 'border-slate-200',   text: 'text-slate-600',   icon: 'text-slate-400',   solid: 'bg-slate-500' },
} as const;

export type StatusVariant = keyof typeof STATUS;

// ── 圆角 ────────────────────────────────────────────
export const R = {
  sm:    'rounded-lg',
  base:  'rounded-xl',
  lg:    'rounded-2xl',
  full:  'rounded-full',
  modal: 'rounded-t-2xl md:rounded-2xl',
} as const;

// ── 过渡 ────────────────────────────────────────────
export const T = {
  fast:   'transition-colors duration-150',
  normal: 'transition-all duration-200',
  slow:   'transition-all duration-300',
} as const;

// ── 阴影 ────────────────────────────────────────────
export const S = {
  sm:   'shadow-sm',
  md:   'shadow-lg',
  lg:   'shadow-xl',
  xl:   'shadow-2xl',
} as const;

// ── Z-Index 层级 ────────────────────────────────────
export const Z = {
  sticky:   'z-10',   // sticky headers/footers
  dropdown: 'z-20',   // dropdowns, popovers
  header:   'z-30',   // mobile headers
  backdrop: 'z-40',   // modal backdrop
  modal:    'z-50',   // primary modals, mobile nav
  modal2:   'z-[60]', // secondary modals (nested)
  toast:    'z-[70]', // toast notifications (always on top)
} as const;

// ── 调色板 ───────────────────────────────────────────
// Dashboard 图表、Tag 颜色等场景的统一色板
export const PALETTE = [
  '#6366f1', '#ef4444', '#22c55e', '#f59e0b', '#8b5cf6',
  '#ec4899', '#14b8a6', '#f97316', '#06b6d4', '#84cc16',
] as const;

// ── 字号语义 ─────────────────────────────────────────
// 移动端优先字号,桌面端用 md: 回到更紧凑密度。
export const FONT = {
  caption:  'text-xs',
  body:     'text-sm',
  input:    'text-base md:text-sm',
  emphasis: 'text-base',
  title:    'text-lg md:text-base font-semibold',
  heading:  'text-xl md:text-lg font-bold',
  display:  'text-2xl font-bold',
} as const;

// ── 触控目标 ─────────────────────────────────────────
// 移动端 44px 最小触控高度,桌面端回到 32px 保持密度。
export const TOUCH = {
  base: 'min-h-[44px] md:min-h-[32px] touch-manipulation',
} as const;

// ── 卡片容器 ─────────────────────────────────────────
// 统一的移动优先卡片外观:ring-1 代替 border,圆角 2xl。
export const CARD = {
  base:        'bg-white rounded-2xl ring-1 ring-slate-100',
  elevated:    'bg-white rounded-2xl shadow-md ring-1 ring-slate-100',
  interactive: 'bg-white rounded-2xl ring-1 ring-slate-100 hover:shadow-sm transition-shadow',
} as const;

// ── 主色调板使用规范 ─────────────────────────────────
/**
 * Primary color palette usage guide
 *
 * 品牌主色 `#FF5E5E` 在 `tailwind.config` 映射为 `primary-500`,
 * 其余色阶配套使用。禁止随意替换色值 —
 * 调整品牌色请改 `tailwind.config` 单一入口。
 *
 * - `primary-500`:主 CTA(Button primary)、active 导航项、关键金额数值
 * - `primary-400`:hover / focus ring、次要强调
 * - `primary-100`:light background(active nav bg、badge bg)
 * - `primary-600`:pressed 状态、深色 CTA、文字按钮 hover 深化
 *
 * 边角场景参考:
 * - 文字引用主色 → `text-primary-500`
 * - 图标引用主色 → `text-primary-500`(实心)或 `text-primary-400`(描边)
 * - 背景高亮 → `bg-primary-100`(浅)或 `bg-primary-500`(实心 CTA)
 * - focus-visible 环 → `ring-primary-400`
 */

